<script setup lang="ts">
import { toRefs } from 'vue';
import Spinner from '../Loader/Spinner.vue';

interface Props {
  text?: string;
  type?: string;
  color?: string;
  size?: string;
  disabled?: boolean;
  iconLeft?: string;
  iconRight?: string;
  fullWidth?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  type: 'Primary',
  color: '',
  size: 'medium',
  disabled: false,
  iconLeft: '',
  iconRight: '',
  fullWidth: false,
  loading: false,
});

const { loading, fullWidth, iconRight, iconLeft, type, text } = toRefs(props);
</script>

<template>
  <button
    type="button"
    :class="[
      'button',
      'button-style-' + type,
      'button-size-' + size,
      color ? 'button-color-' + color : '',
      fullWidth ? 'button-full-width' : '',
      { 'no-text': text === '' },
      { 'button-loading': loading },
    ]"
    :disabled="disabled"
  >
    <span class="button-inner">
      <span class="button-text">
        <span :class="loading ? 'spinner-visible' : 'spinner-hidden'">
          <Spinner :size="18" class="mr-1" />
        </span>
        <i class="button-icon-left material-icons material-icons-round">{{ iconLeft }}</i>
        <span v-if="text" class="mx-1">{{ text }}</span>
        <i class="button-icon-right material-icons material-icons-round">{{ iconRight }}</i>
      </span>
      <span class="button-details"></span>
      <span class="button-outline"></span>
    </span>
  </button>
</template>

<style scoped lang="scss">
.button {
  background: none;
  display: flex;
  align-items: flex-end;
  padding: 0px;
  margin: 0px;
  border: none;
  cursor: pointer;
  color: var(--primary-700);
  font-weight: 600;
  line-height: 1.125;
  border-radius: 8px;
  position: relative;
  transition: padding 0.08s ease-in-out, height 0.08s ease-in-out;
  padding-bottom: 0.25rem;

  &.button-full-width {
    width: 100%;
    .button-inner {
      width: 100%;
      justify-content: center;
    }
  }

  &:disabled {
    pointer-events: none;
    box-shadow: none;
  }

  &::before {
    content: '';
    border-radius: 0px 0px 8px 8px;
    position: absolute;
    bottom: 0px;
    left: 0px;
    height: 1rem;
    width: 100%;
  }

  .button-outline {
    position: absolute;
    top: -0.5rem;
    left: -0.5rem;
    height: calc(100% + 1.25rem);
    width: calc(100% + 1rem);
    border: 0.25rem solid var(--primary-100);
    border-radius: 0.75rem;
    pointer-events: none;
    transition: 0.08s ease-in-out;
    padding-bottom: 0.25rem;
    opacity: 0;
  }

  .button-inner {
    background: white;
    padding: 6.5px 9px 5.5px 9px;
    border-radius: 0.5rem;
    transition: 0.08s ease-in-out;
    position: relative;
    display: flex;
    align-items: center;
    .button-text {
      font-size: 14px;
      display: flex;
      align-items: center;
      white-space: nowrap;
      .button-icon-left {
        max-width: 14px;
        overflow: hidden;
        font-size: inherit;
        margin-bottom: 1px;
        color: inherit;
      }
      .button-icon-right {
        max-width: 14px;
        overflow: hidden;
        font-size: inherit;
        margin-bottom: 1px;
        color: inherit;
      }
    }
  }

  .button-details {
    position: absolute;
    top: -0.25rem;
    left: -0.25rem;
    width: calc(100% + 0.5rem);
    height: calc(100% + 0.5rem);
    border-radius: 0.5rem;
    transition: 0.08s ease-in-out;
    pointer-events: none;
  }

  &:hover {
    .button-outline {
      padding-bottom: 0px;
    }
  }

  &:focus {
    outline: none;
    .button-outline {
      opacity: 1;
    }
  }

  &:active {
    outline: none;
    padding-bottom: 0px;
    .button-outline {
      height: calc(100% + 1rem);
    }
  }

  &.no-text {
    .button-inner {
      .button-text {
        .button-icon-left,
        .button-icon-right {
          margin: 0px;
        }
      }
    }
  }
}

.button.button-style-primary,
.button-set.button-style-primary .button {
  color: white;
  &:disabled {
    color: var(--surface-200);
    .button-inner {
      background: var(--surface-100);
      border-color: var(--surface-200);
      .button-text {
        .button-icon-left,
        .button-icon-right {
          color: var(--surface-200);
        }
      }
    }
    .button-details {
      box-shadow: inset 0 2px 2px var(--surface-100), inset 0px -2px 2px var(--surface-300);
    }
    &::before {
      background: var(--surface-300);
    }
  }
  &::before {
    background: var(--primary-900);
  }
  .button-inner {
    background: var(--primary-700);
    border: 0.25rem solid var(--primary-800);
    .button-text {
      .button-icon-left {
        color: var(--primary-300);
      }
      .button-icon-right {
        color: var(--primary-300);
      }
    }
  }
  .button-details {
    box-shadow: inset 0 1.5px 1.5px var(--primary-600), inset 0px -1.5px 1.5px var(--primary-900);
    &::after,
    &::before {
      background: var(--primary-800);
    }
  }
  &:hover {
    .button-inner {
      background: var(--primary-700);
    }
  }
}

.button.button-style-secondary,
.button-set.button-style-secondary .button {
  color: var(--primary-700);
  &:disabled {
    color: var(--surface-200);
    .button-inner {
      border-color: var(--surface-100);
      .button-text {
        .button-icon-left,
        .button-icon-right {
          color: var(--surface-200);
        }
      }
    }
    .button-details {
      box-shadow: inset 0 2px 2px var(--surface-100), inset 0px -2px 2px var(--surface-200);
    }
    &::before {
      background: var(--surface-200);
    }
  }
  &::before {
    background: var(--primary-500);
  }
  .button-inner {
    border: 0.25rem solid var(--primary-300);
    .button-text {
      .button-icon-left {
        color: var(--primary-500);
      }
      .button-icon-right {
        color: var(--primary-500);
      }
    }
  }
  .button-details {
    box-shadow: inset 0 2px 2px var(--primary-200), inset 0px -2px 2px var(--primary-500);
    &::after,
    &::before {
      background: var(--primary-300);
    }
  }
  &:hover {
    .button-inner {
      background: var(--primary-100);
    }
  }
}

.button.button-style-alert,
.button-set.button-style-alert .button {
  color: $white;
  &:disabled {
    color: var(--surface-200);
    .button-inner {
      border-color: var(--surface-100);
      border: 0.25rem solid var(--surface-100);
      background: white;
      .button-text {
        .button-icon-left,
        .button-icon-right {
          color: var(--surface-200);
        }
      }
    }
    .button-details {
      box-shadow: inset 0 2px 2px rgba(255, 255, 255, 0.7), inset 0px -2px 2px var(--surface-200);
    }
    &::before {
      background: var(--surface-200);
    }
  }
  &::before {
    background: #922a0d;
  }
  .button-inner {
    border: 0.25rem solid $errorColorDark;
    background: $errorColor;
    .button-text {
      .button-icon-left {
        color: $redLight;
      }
      .button-icon-right {
        color: $redLight;
      }
    }
  }
  .button-details {
    box-shadow: inset 0px -2px 2px rgba(130, 37, 8, 0.75), inset 0px 2px 2px rgba(255, 255, 255, 0.25);
    &::after,
    &::before {
      background: $errorColor;
    }
  }
  &:hover {
    .button-inner {
      background: $errorColor;
    }
  }
}

.button.button-style-tertiary,
.button-set.button-style-tertiary .button {
  &:disabled {
    color: var(--surface-200);
    .button-inner {
      border-color: var(--surface-100);
      .button-text {
        .button-icon-left,
        .button-icon-right {
          color: var(--surface-200);
        }
      }
    }
    .button-details {
      box-shadow: inset 0 2px 2px rgba(255, 255, 255, 0.7), inset 0px -2px 2px var(--surface-200);
    }
    &::before {
      background: var(--surface-200);
    }
  }
  &::before {
    background: var(--surface-200);
  }
  .button-inner {
    border: 0.25rem solid var(--surface-100);
    .button-text {
      .button-icon-left {
        color: var(--primary-500);
      }
      .button-icon-right {
        color: var(--primary-500);
      }
    }
  }
  .button-details {
    box-shadow: inset 0 2px 2px rgba(255, 255, 255, 0.7), inset 0px -2px 2px var(--surface-200);
    &::after,
    &::before {
      background: var(--surface-200);
    }
  }
  &:hover {
    .button-inner {
      background: var(--surface-100);
    }
  }
}

.button.button-style-text {
  padding-bottom: 0px;
  &:disabled {
    .button-inner {
      color: var(--surface-200);
      background: var(--surface-100);
      border-color: var(--surface-100);
      height: 100%;
      .button-text {
        .button-icon-left,
        .button-icon-right {
          color: var(--surface-200);
        }
      }
    }
  }
  .button-inner {
    border: 0.25rem solid white;
    .button-details {
      display: none;
    }
  }
  .button-outline {
    height: calc(100% + 1rem);
  }
  &:hover {
    .button-inner {
      background: var(--surface-100);
      border: 0.25rem solid var(--surface-100);
    }
  }
  &:focus {
    .button-inner {
      background: var(--surface-100);
      border: 0.25rem solid var(--surface-100);
    }
  }
  &:active {
    .button-inner {
      background: var(--primary-100);
      border: 0.25rem solid var(--primary-100);
    }
  }
}

.button.button-style-text,
.button-set.button-style-text .button {
  box-shadow: none;
}

.button.button-style-text.button-size-medium .button-inner {
  height: 36px;
}

.button.button-style-link {
  background: none;
  padding: 0px;
  align-items: center;
  .button-inner {
    background: none;
    transition: 0.2s ease;
    padding: 0px;
    .button-text {
      span {
        margin: 1px 0px 0px 0px !important;
      }
      .button-icon-left {
        margin-bottom: 0px;
      }
    }
  }
  &.button-size-medium {
    height: 1rem;
    .button-inner {
      height: 1rem;
    }
  }
  &:hover {
    .button-inner {
      .button-text {
        opacity: 0.75;
      }
    }
  }
  &:focus {
    .button-outline {
      display: none;
    }
  }
}

.button.button-size-medium,
.button-set.button-size-medium .button {
  height: 36px;
  .button-inner {
    height: 32px;
  }
}

.button.button-size-large,
.button-set.button-size-large .button {
  height: 48px;
  .button-inner {
    height: 44px;
    .button-text {
      font-size: 18px;
      .button-icon-left,
      .button-icon-right {
        max-width: 18px;
      }
      span {
        font-size: inherit;
      }
    }
  }
}

.button.button-size-small,
.button-set.button-size-small .button {
  height: 28px;
  .button-inner {
    height: 24px;
    .button-text {
      font-size: 12px;
    }
  }
}

.button.button-color-yellow {
  .button-inner {
    .button-text {
      color: $orangeDark;
    }
  }
}

.button .spinner-visible {
  width: 18px;
  transition: 0.4s ease;
}
.button .spinner-hidden {
  width: 0px;
  overflow: hidden;
}

.button.button-loading {
  pointer-events: none;
  opacity: 0.75;
}
</style>
